import {Injectable} from '@angular/core';
import * as CookieConsent from 'vanilla-cookieconsent';
import {PlatformService} from './platform.service';
import {AppTrackingTransparency} from '@capgo/capacitor-app-tracking-transparency';
// @ts-expect-error We somehow needed to import this global type
import Gtag = Gtag.Gtag;

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

@Injectable({
  providedIn: 'root'
})
export class CookieConsentService {
  private _defaultConsent = {
    ['functionality_storage']: 'granted',
    ['security_storage']: 'granted',
    ['anonymous_statistics']: 'granted',
    ['analytics_storage']: 'granted',
    ['enhanced_analytics_storage']: 'denied',
    ['personalization_storage']: 'denied',
    ['ad_user_data']: 'denied',
    ['ad_personalization']: 'denied',
    ['ad_storage']: 'denied',
  };
  // Categories that may track users. On iOS, Apple only allows these after the user granted
  // permission through the App Tracking Transparency (ATT) prompt.
  private _trackingCategories = ['enhanced_analytics', 'marketing'];
  private _trackingAllowed = true;
  private _firstConsentSet = false;

  constructor(private _platformService: PlatformService) {
  }

  async initConsent() {
    if (this._platformService.isIos()) {
      // getStatus() never shows the prompt; it is requested in initConsentManager().
      const {status} = await AppTrackingTransparency.getStatus();
      this._trackingAllowed = status === 'authorized';
    }
    if (!this._consentPreferencesSet()) {
      return;
    }
    this._handleConsent(this._getPreferences());
    this._firstConsentSet = true;
  }

  async initConsentManager() {
    if (this._platformService.isIos()) {
      // Shows the ATT prompt when the user has not chosen yet, otherwise returns the earlier choice.
      const {status} = await AppTrackingTransparency.requestPermission();
      this._trackingAllowed = status === 'authorized';
    }

    CookieConsent.run({
      // Without ATT permission the optional categories are left out, so there is nothing to ask.
      autoShow: this._trackingAllowed,
      cookie: {
        useLocalStorage: this._platformService.isNative(),
      },
      hideFromBots: true,
      guiOptions: {
        consentModal: {
          layout: 'box',
          position: 'bottom left',
          equalWeightButtons: false,
          flipButtons: false
        },
        preferencesModal: {
          layout: 'box',
          equalWeightButtons: false,
          flipButtons: false
        }
      },
      categories: {
        necessary: {
          enabled: true,
          readOnly: true
        },
        analytics: {
          enabled: true,
          readOnly: true
        },
        ["enhanced_analytics"]: {
          enabled: false,
          readOnly: false
        },
        marketing: {
          enabled: false,
          readOnly: false
        }
      },
      onConsent: ({cookie}) => {
        if (this._firstConsentSet) {
          return;
        }
        this._handleConsent(cookie.categories);
      },
      onChange: ({cookie}) => {
        this._handleConsent(cookie.categories);
      },
      language: {
        default: 'nl',
        translations: {
          nl: {
            consentModal: {
              title: 'Wij gebruiken cookies',
              description: 'Deze website maakt gebruik van verschillende soorten cookies. Voor sommige soorten hebben we je toestemming nodig.<br><br>' +
                'De noodzakelijke cookies en anonieme statistieken zijn altijd automatisch actief; als je op "accepteer alle cookies" klikt, accepteer je ook de optionele cookies.' +
                'Wanneer je zelf wilt kiezen welke cookies je aanzet, klik je op “cookie instellingen”.',
              acceptAllBtn: 'Accepteer alle cookies',
              acceptNecessaryBtn: 'Weiger optionele cookies',
              showPreferencesBtn: 'Cookie instellingen'
            },
            preferencesModal: {
              title: 'Cookie-instellingen',
              acceptAllBtn: 'Accepteer alle cookies',
              acceptNecessaryBtn: 'Weiger optionele cookies',
              savePreferencesBtn: 'Instellingen opslaan',
              closeIconLabel: 'Venster sluiten',
              sections: [
                {
                  title: 'Noodzakelijk',
                  description: 'Noodzakelijke cookies gebruiken we om de basisfuncties van de site te kunnen laten draaien. Deze cookies verzamelen nooit persoonsgegevens.',
                  linkedCategory: 'necessary'
                },
                {
                  title: 'Anonieme statistieken',
                  description: 'Deze cookies verzamelen anoniem gegevens over hoe bezoekers onze website gebruiken, zodat we onze website verder kunnen verbeteren.',
                  linkedCategory: 'analytics'
                },
                {
                  title: 'Analyse',
                  description: 'Deze cookies verzamelen gegevens over hoe bezoekers onze website gebruiken, zodat we onze website verder kunnen verbeteren.',
                  linkedCategory: 'enhanced_analytics'
                },
                {
                  title: 'Marketing',
                  description: 'Deze cookies maken het mogelijk om embeds van YouTube, LinkedIn, Facebook, Instagram etc. te tonen in artikelen. Ook kunnen deze cookies worden gebruikt om gepersonaliseerde advertenties te tonen en daarom kunnen deze cookies je surfgedrag op verschillende websites volgen.',
                  linkedCategory: 'marketing'
                },
              ].filter(section => this._trackingAllowed || !this._trackingCategories.includes(section.linkedCategory))
            }
          }
        }
      }
    });
  }

  private _handleConsent(categories: string[]) {
    if (this._platformService.isServer()) {
      return;
    }
    if (!this._trackingAllowed) {
      // Also covers consent given before the user denied ATT in the iOS settings.
      categories = categories.filter(category => !this._trackingCategories.includes(category));
    }

    function gtag(): Gtag {
      // eslint-disable-next-line prefer-rest-params
      (window.dataLayer ?? []).push(arguments);
    }

    const storageConsent = {...this._defaultConsent};
    let adsDataRedaction = true;
    const updateEvents = [
      'consent_updated'
    ];

    if (categories.includes('enhanced_analytics')) {
      storageConsent.enhanced_analytics_storage = 'granted';
      updateEvents.push('analytics_granted');
    }
    if (categories.includes('marketing')) {
      storageConsent.personalization_storage = 'granted';
      storageConsent.ad_storage = 'granted';
      storageConsent.ad_user_data = 'granted';
      storageConsent.ad_personalization = 'granted';
      adsDataRedaction = false;
      updateEvents.push('marketing_granted');
    }
    if (updateEvents.length >= 3) {
      updateEvents.push('everything_granted');
    }

    // @ts-expect-error unfortunately event with types, gtag does not behave as expected
    gtag('consent', 'update', storageConsent);

    if (adsDataRedaction) {
      // @ts-expect-error unfortunately event with types, gtag does not behave as expected
      gtag('set', 'ads_data_redaction', true);
    }

    if (storageConsent.enhanced_analytics_storage === 'granted') {
      // @ts-expect-error unfortunately event with types, gtag does not behave as expected
      gtag('config', 'GTM-KXZG92GG', {['anonymize_ip']: 'false'});
    } else {
      // @ts-expect-error unfortunately event with types, gtag does not behave as expected
      gtag('config', 'GTM-KXZG92GG', {['anonymize_ip']: 'true'});
    }

    updateEvents.forEach(eventName => {
      // @ts-expect-error unfortunately event with types, gtag does not behave as expected
      gtag('event', eventName);
    });
  }

  private _consentPreferencesSet() {
    if (this._platformService.isServer()) {
      return false;
    }
    if (!this._platformService.isNative()) {
      return Object.keys(CookieConsent.getCookie()).length > 0;
    }
    return localStorage.getItem('cc_cookie') !== null;
  }

  private _getPreferences(): string[] {
    if (this._platformService.isServer()) {
      return [];
    }
    if (!this._platformService.isNative()) {
      return CookieConsent.getCookie().categories;
    }
    const localStorageCookie = localStorage.getItem('cc_cookie');
    if (!localStorageCookie) {
      return [];
    }
    try {
      const cookie = JSON.parse(localStorageCookie);
      if (!cookie.categories) {
        return [];
      }
      return cookie.categories;
    } catch {
      return [];
    }
  }

  showPreferences() {
    CookieConsent.showPreferences();
  }
}
